/* JUGO DE OLIVA — interactions. No dependencies, progressive enhancement.
   Everything degrades to a fully usable static page without JS.
   Performance rules: native scrolling, one rAF loop that reads first and writes second,
   transform/opacity only, nothing runs while the page is idle. */
(() => {
  'use strict';

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const root = document.documentElement;
  const esc = value => String(value == null ? '' : value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
  const mqFine = matchMedia('(hover: hover) and (pointer: fine)');
  const mqDesktop = matchMedia('(min-width: 1024px)');
  const reduced = () => mqReduce.matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  /* ---------------------------------------------------------------- i18n (injected by build.rb) */
  let I18N = { lang: 'en', base: '', shop: 'https://www.jugodeoliva.com', s: {}, search: [] };
  try { I18N = JSON.parse($('#i18n').textContent); } catch (e) { /* static fallback */ }
  const S = I18N.s;
  const MARKET = I18N.market || { country: 'CZ', currency: 'CZK', symbol: 'Kč', decimals: 0 };
  // plural forms [one, few, many]: cs/sk few = 2–4 · pl few = 2–4 except 12–14 (22, 23, 24…)
  const pluralIndex = n => {
    if (n === 1) return 0;
    if (I18N.plural === 'pl') return n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 1 : 2;
    if (I18N.plural === 'west-slavic') return n >= 2 && n <= 4 ? 1 : 2;
    return 2;
  };
  const plural = (forms, n) => String(Array.isArray(forms) ? forms[pluralIndex(n)] : (forms || '%d')).replace('%d', n);
  const dec = s => (I18N.comma ? String(s).replace('.', ',') : String(s));
  // money in the page's market: "1 118 Kč" · "20,95 €" · "102,00 zł"
  const kc = n => {
    const [whole, frac] = (Math.round(n * 100) / 100).toFixed(MARKET.decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${frac ? grouped + (I18N.comma ? ',' : '.') + frac : grouped} ${MARKET.symbol}`;
  };
  const fold = s => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  /* ---------------------------------------------------------------- Scroll loop
     Subscribers read layout and return an optional write function; all reads run before all writes. */
  const scrollSubs = new Set();
  let ticking = false;
  const runScroll = () => {
    ticking = false;
    const y = window.scrollY;
    const writes = [];
    scrollSubs.forEach(fn => { const w = fn(y); if (w) writes.push(w); });
    writes.forEach(w => w());
  };
  const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(runScroll); } };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll, { passive: true });

  let syncVideos = () => {}; // set by the background-video block below

  /* ---------------------------------------------------------------- Split words */
  const splitWords = el => {
    let i = 0;
    const walk = node => {
      [...node.childNodes].forEach(child => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach(p => {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(p)); return; }
            const w = document.createElement('span'); w.className = 'w';
            const inner = document.createElement('span'); inner.textContent = p; inner.style.setProperty('--wi', i++);
            w.appendChild(inner); frag.appendChild(w);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && !child.matches('br, svg')) walk(child);
      });
    };
    walk(el);
  };
  $$('[data-split]').forEach(splitWords);

  /* ---------------------------------------------------------------- Hero: the second sentence
     sits exactly under the line above it — same left edge, same right edge. Its size is whatever
     makes those two match, so it keeps working in every language and at every width. */
  const fitHeroSub = () => {
    const sub = $('.hero__title .line--sub');
    const above = sub && sub.previousElementSibling;
    if (!sub || !above) return;
    // glyph extent, not the box: the split words carry padding that would skew the match
    const extent = el => {
      const parts = $$('.w > span', el);
      if (!parts.length) { const r = document.createRange(); r.selectNodeContents(el); const b = r.getBoundingClientRect(); return { left: b.left, right: b.right }; }
      let l = Infinity, r = -Infinity;
      parts.forEach(p => { const b = p.getBoundingClientRect(); l = Math.min(l, b.left); r = Math.max(r, b.right); });
      return { left: l, right: r };
    };
    sub.style.fontSize = '';
    sub.style.paddingLeft = '';
    // the width does not scale perfectly linearly with the font size (tracking, word spacing,
    // hinting), so correct it a few times — two passes are usually already exact
    for (let i = 0; i < 4; i++) {
      const target = extent(above), cur = extent(sub);
      const w = cur.right - cur.left, want = target.right - target.left;
      if (!w || !want) return;
      if (Math.abs(want - w) < 0.5) break;
      sub.style.fontSize = (parseFloat(getComputedStyle(sub).fontSize) * (want / w)) + 'px';
    }
    const target = extent(above), moved = extent(sub);
    sub.style.paddingLeft = (parseFloat(getComputedStyle(sub).paddingLeft) + (target.left - moved.left)) + 'px';
  };
  fitHeroSub();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitHeroSub);
  let subTimer;
  addEventListener('resize', () => { clearTimeout(subTimer); subTimer = setTimeout(fitHeroSub, 150); });

  /* ---------------------------------------------------------------- Reveal on scroll */
  const revealEls = $$('[data-reveal], [data-split], [data-inview]');
  if ('IntersectionObserver' in window && !reduced()) {
    const io = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });
    const vh = innerHeight, atTop = window.scrollY < 10;
    const first = [];
    revealEls.forEach(el => { if (atTop && el.getBoundingClientRect().top < vh) first.push(el); else io.observe(el); });
    requestAnimationFrame(() => first.forEach(el => el.classList.add('is-in')));
  } else revealEls.forEach(el => el.classList.add('is-in'));

  /* ---------------------------------------------------------------- Header */
  const hdr = $('[data-hdr]');
  if (hdr) {
    let lastY = window.scrollY, scrolled = null, hidden = null;
    const update = y => {
      const s = y > 24;
      let h = hidden;
      if (y > lastY + 6 && y > 480 && !root.classList.contains('has-dialog')) h = true;
      else if (y < lastY - 6 || y < 480) h = false;
      if (Math.abs(y - lastY) > 6) lastY = y;
      if (s === scrolled && h === hidden) return null;
      return () => {
        if (s !== scrolled) { hdr.classList.toggle('is-scrolled', s); scrolled = s; }
        if (h !== hidden) { hdr.classList.toggle('is-hidden', h); hidden = h; }
      };
    };
    scrollSubs.add(update);
    const w = update(lastY); if (w) w();
  }

  /* ---------------------------------------------------------------- Parallax (desktop only)
     data-parallax="0.06" (y) · data-parallax-x="-0.3" (x). Only elements on screen are measured. */
  const plx = $$('[data-parallax], [data-parallax-x]');
  if (plx.length && !reduced()) {
    const visible = new Set();
    const vio = new IntersectionObserver(es => es.forEach(e => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target))), { rootMargin: '10% 0px' });
    plx.forEach(el => vio.observe(el));
    const measure = () => {
      if (!visible.size) return null;
      const vh = innerHeight, desk = mqDesktop.matches;
      const out = [];
      visible.forEach(el => {
        const r = (el.parentElement || el).getBoundingClientRect();
        if (el.hasAttribute('data-parallax-x')) {
          const p = clamp((vh - r.top) / (vh + r.height), 0, 1);
          out.push([el, `translate3d(${((p - 0.5) * parseFloat(el.dataset.parallaxX) * 100).toFixed(2)}%,0,0)`]);
        } else if (desk) {
          out.push([el, `translate3d(0,${((r.top + r.height / 2 - vh / 2) * -parseFloat(el.dataset.parallax)).toFixed(1)}px,0)`]);
        }
      });
      return () => out.forEach(([el, t]) => { el.style.transform = t; });
    };
    scrollSubs.add(measure);
    requestAnimationFrame(() => { const w = measure(); if (w) w(); });
  }

  /* ---------------------------------------------------------------- Manifesto: words light up with scroll */
  $$('[data-scroll-words]').forEach(el => {
    el.querySelectorAll('p').forEach(p => {
      const html = [];
      p.childNodes.forEach(n => {
        if (n.nodeType === 3) html.push(n.textContent.replace(/(\S+)/g, '<span class="sw">$1</span>'));
        else if (n.nodeType === 1) { n.innerHTML = n.innerHTML.replace(/(\S+)/g, '<span class="sw">$1</span>'); html.push(n.outerHTML); }
      });
      p.innerHTML = html.join('');
    });
    const words = [...el.querySelectorAll('.sw')];
    if (reduced()) { words.forEach(w => w.classList.add('on')); return; }
    let lit = 0, near = false;
    new IntersectionObserver(([e]) => { near = e.isIntersecting; if (near) onScroll(); }, { rootMargin: '50% 0px' }).observe(el);
    const update = () => {
      if (!near) return null;
      const r = el.getBoundingClientRect();
      const n = Math.round(clamp((innerHeight * 0.8 - r.top) / (r.height * 0.9), 0, 1) * words.length);
      if (n === lit) return null;
      const from = lit;
      lit = n;
      return () => {
        if (n > from) for (let i = from; i < n; i++) words[i].classList.add('on');
        else for (let i = n; i < from; i++) words[i].classList.remove('on');
      };
    };
    scrollSubs.add(update);
  });

  /* ---------------------------------------------------------------- Sticky steps */
  $$('[data-steps]').forEach(section => {
    const steps = $$('[data-step]', section);
    const media = $$('[data-step-media]', section);
    const counter = $('[data-step-counter]', section);
    let current = -1;
    const set = i => {
      if (i === current) return;
      current = i;
      steps.forEach((s, k) => s.classList.toggle('is-active', k === i));
      media.forEach((m, k) => m.classList.toggle('is-active', k === i));
      if (counter) counter.textContent = `${String(i + 1).padStart(2, '0')} / ${String(steps.length).padStart(2, '0')}`;
      syncVideos();
    };
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) set(steps.indexOf(e.target)); }), { rootMargin: '-45% 0px -45% 0px' });
    steps.forEach(s => io.observe(s));
    set(0);
  });

  /* ---------------------------------------------------------------- Count up (0.36 etc.) */
  $$('[data-count]').forEach(el => {
    if (reduced()) return;
    const end = parseFloat(el.dataset.count), places = (el.dataset.count.split('.')[1] || '').length;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return; io.disconnect();
      const t0 = performance.now(), dur = 1200;
      const tick = t => { const k = clamp((t - t0) / dur, 0, 1); el.textContent = dec((end * (1 - Math.pow(1 - k, 4))).toFixed(places)); if (k < 1) requestAnimationFrame(tick); };
      requestAnimationFrame(tick);
    }, { threshold: 0.6 });
    io.observe(el);
  });

  /* ---------------------------------------------------------------- Olive cursor (fine pointers only)
     One transformed wrapper; runs only while the pointer moves.
     Modal dialogs (cart, menu, search) and the language popover render in the browser's top layer,
     above every z-index — so the cursor lives in the top layer too (manual popover) and is
     re-raised whenever one of them opens. Without popover support, dialogs get the system cursor. */
  let raiseCursor = () => {};
  if (mqFine.matches && !reduced()) {
    const cur = $('.cursor');
    const label = $('.cursor__label', cur);
    let x = -100, y = -100, cx = -100, cy = -100, shown = false, raf = 0;
    root.classList.add('has-cursor');
    if (typeof cur.showPopover === 'function') {
      cur.setAttribute('popover', 'manual');
      raiseCursor = () => {
        try { if (cur.matches(':popover-open')) cur.hidePopover(); cur.showPopover(); } catch (e) { /* detached */ }
      };
      raiseCursor();
      $$('[popover]:not(.cursor)').forEach(p => p.addEventListener('toggle', e => { if (e.newState === 'open') raiseCursor(); }));
    } else {
      root.classList.add('cursor-no-top-layer');
    }
    const loop = () => {
      cx += (x - cx) * 0.35; cy += (y - cy) * 0.35;
      if (Math.abs(x - cx) + Math.abs(y - cy) < 0.2) { cx = x; cy = y; raf = 0; } else raf = requestAnimationFrame(loop);
      cur.style.transform = `translate3d(${cx}px,${cy}px,0)`;
    };
    addEventListener('pointermove', e => {
      if (e.pointerType !== 'mouse') return;
      x = e.clientX; y = e.clientY;
      if (!shown) { cx = x; cy = y; shown = true; cur.style.opacity = 1; }
      if (!raf) raf = requestAnimationFrame(loop);
    }, { passive: true });
    document.addEventListener('pointerleave', () => { cur.style.opacity = 0; shown = false; });
    addEventListener('pointerdown', () => cur.classList.add('is-down'));
    addEventListener('pointerup', () => cur.classList.remove('is-down'));
    let lastTarget = null;
    document.addEventListener('pointerover', e => {
      const t = e.target;
      if (t === lastTarget) return;
      lastTarget = t;
      const media = t.closest('[data-cursor]');
      const text = t.closest('input, textarea, select, [contenteditable]');
      const link = t.closest('a, button, summary, label, [role="button"]');
      const dark = t.closest('.t-green, .t-deep, .t-rose');
      cur.classList.toggle('is-media', !!media && !text);
      cur.classList.toggle('is-link', !!link && !media && !text);
      cur.classList.toggle('is-text', !!text);
      cur.classList.toggle('is-dark', !!dark && !media);
      label.textContent = media ? media.dataset.cursor : '';
    });
  }

  /* ---------------------------------------------------------------- Dialogs (menu, cart, search) */
  const unlock = () => { if (!$('dialog[open]')) root.classList.remove('has-dialog'); };
  const closeDialog = d => { d.close(); unlock(); };
  const openDialog = id => {
    const d = document.getElementById(id); if (!d || d.open) return;
    $$('dialog[open]').forEach(o => o.close());
    try { const loc = document.getElementById('loc'); if (loc && loc.hidePopover) loc.hidePopover(); } catch (e) { /* not open */ }
    d.showModal(); root.classList.add('has-dialog');
    raiseCursor(); // keep the olive cursor above the newly opened dialog
    if (id === 'search') setTimeout(() => { const i = $('[data-search-input]'); if (i) i.focus(); }, 60);
    if (id === 'menu') $$('.menu__nav li', d).forEach((li, i) => li.style.setProperty('--i', i));
  };
  $$('dialog').forEach(d => {
    d.addEventListener('close', unlock);
    d.addEventListener('cancel', () => setTimeout(unlock));
    d.addEventListener('click', e => { if (e.target === d || e.target.closest('a[href]')) closeDialog(d); });
    $$('[data-close]', d).forEach(b => b.addEventListener('click', () => closeDialog(d)));
  });
  document.addEventListener('click', e => {
    const t = e.target.closest('[data-dialog]'); if (!t) return;
    e.preventDefault(); openDialog(t.dataset.dialog);
  });

  /* ---------------------------------------------------------------- Search (client-side index, accent-insensitive) */
  const input = $('[data-search-input]'), results = $('[data-search-results]');
  if (input && results) {
    const initial = results.innerHTML;
    let searchTimer, searchController, revision = 0;
    const renderHits = hits => {
      results.innerHTML = hits.length
        ? `<p class="eyebrow">${esc(plural(S.results, hits.length))}</p><ul role="list" class="search__list">${hits.map(([title, url, cat]) => `<li><a href="${esc(url)}"><span>${esc(title)}</span><small>${esc(cat)}</small></a></li>`).join('')}</ul>`
        : `<p class="eyebrow">${esc(S.noResults || 'No results')}</p><p class="muted">${esc(S.noResultsHint || '')}</p>`;
    };
    input.addEventListener('input', () => {
      const query = input.value.trim(), currentRevision = ++revision;
      clearTimeout(searchTimer);
      if (searchController) searchController.abort();
      if (!query) { results.innerHTML = initial; return; }
      const shop = window.JDO_SHOPIFY;
      if (!shop) {
        const q = fold(query);
        renderHits((I18N.search || []).filter(([title, , , kw]) => fold(`${title} ${kw}`).includes(q)));
        return;
      }
      // Enter always submits the complete Shopify search form, including during a request.
      results.innerHTML = '';
      searchTimer = setTimeout(async () => {
        searchController = new AbortController();
        try {
          const hits = await shop.search(query, searchController.signal);
          if (currentRevision === revision) renderHits(hits);
        } catch (error) {
          if (error.name !== 'AbortError' && currentRevision === revision) {
            results.textContent = S.noResultsHint || '';
          }
        }
      }, 180);
    });
    const form = $('[data-search]');
    if (form) form.addEventListener('submit', e => { if (!window.JDO_SHOPIFY) { const firstHit = $('.search__list a', results); if (firstHit) { e.preventDefault(); location.href = firstHit.href; } } });
  }

  /* ---------------------------------------------------------------- Cart
     Prototype cart in localStorage (shared by both languages). Checkout hands off to the live Shopify
     store via the cart permalink /cart/{variant}:{qty} (under the page's locale) — no checkout rebuild needed. */
  const CART_KEY = `jdo-cart-${MARKET.currency}`; // one cart per currency, so prices never mix
  // The Shopify theme (assets/shopify-cart.js) sets this and takes the cart over: the same drawer then
  // shows the real /cart.js lines and checkout goes to /checkout. Undefined here, in the static prototype.
  const STORE = window.JDO_SHOPIFY || null;
  // variant ID => { opt, img, price } for this market (build.rb); lines keep their own price as a fallback
  const VARIANTS = I18N.variants || {};
  const info = l => ((STORE || l.opt) ? l : VARIANTS[l.id]) || { opt: '500 ml', img: 'bottle-studio', price: l.price };
  const lineImg = l => info(l).imgUrl || I18N.fallbackImage || `${I18N.base}assets/img/${info(l).img}-640.webp`;
  // IDs are strings: Shopify variant IDs, or "new-<key>" for variants not in Shopify yet (older carts stored numbers).
  // Lines whose variant no longer exists (a withdrawn option) are dropped from carts saved earlier.
  const readCart = () => {
    if (STORE) return STORE.lines().map(line => ({ ...line }));            // Shopify holds the cart; the adapter keeps a synced copy
    let raw;
    try { raw = JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch (e) { return []; }
    if (!Array.isArray(raw)) return [];
    const known = Object.keys(VARIANTS).length;
    const lines = raw.map(l => ({ ...l, id: String(l.id) })).filter(l => !known || VARIANTS[l.id]);
    if (lines.length !== raw.length) { try { localStorage.setItem(CART_KEY, JSON.stringify(lines)); } catch (e) { /* private mode */ } }
    return lines;
  };
  let cart = readCart();
  const showCartError = error => {
    let message = $('[data-cart-error]');
    if (!message) {
      message = document.createElement('p');
      message.className = 'muted small'; message.dataset.cartError = '';
      message.setAttribute('role', 'alert');
      const body = $('#cart .drawer__body'); if (!body) return;
      body.prepend(message); openDialog('cart');
    }
    message.hidden = false; message.textContent = error.message || 'Požadavek se nepodařilo dokončit.';
  };
  const clearCartError = () => $$('[data-cart-error]').forEach(el => { el.hidden = true; el.textContent = ''; });
  const saveCart = () => {
    if (STORE) { clearCartError(); STORE.write(cart).then(lines => { cart = lines.map(line => ({ ...line })); renderCart(); }).catch(error => { cart = readCart(); renderCart(); showCartError(error); }); renderCart(); return; }
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch (e) { /* private mode */ }
    renderCart();
  };
  const icon = name => `<svg class="i" aria-hidden="true"><use href="#i-${name}"/></svg>`;
  const renderCart = () => {
    const count = cart.reduce((s, l) => s + l.qty, 0);
    const subtotal = cart.reduce((s, l) => s + l.qty * info(l).price, 0);
    $$('[data-cart-count]').forEach(el => { el.textContent = count; });
    $$('.hdr__cart').forEach(b => b.setAttribute('aria-label', `${S.openCart || 'Open cart'}, ${plural(S.items, count)}`));
    const lines = $('[data-cart-lines]'); if (!lines) return;
    $('[data-cart-empty]').hidden = cart.length > 0;
    $('[data-cart-foot]').hidden = cart.length === 0;
    $('[data-cart-subtotal]').textContent = kc(subtotal);
    // ?country= selects the Shopify Market (EUR for SK, PLN for PL); CZ is the store default
    const country = MARKET.country && MARKET.country !== 'CZ' ? `?country=${MARKET.country}` : '';
    $('[data-checkout]').href = STORE ? STORE.checkoutUrl
      : (cart.length ? `${I18N.shop}/cart/${cart.map(l => `${l.id}:${l.qty}`).join(',')}` : `${I18N.shop}/cart`) + country;
    lines.innerHTML = cart.map(l => `
      <li class="cart-line" data-line="${esc(l.key || l.id)}">
        <div class="cart-line__img"><img src="${esc(lineImg(l))}" alt="" width="640" height="853" loading="lazy"></div>
        <div>
          <p class="cart-line__title">${esc(l.title || S.product || 'Extra Virgin Olive Oil')}</p>
          <p class="cart-line__opt">${esc(info(l).opt)}</p>
          <div class="qty" role="group" aria-label="${S.qty}">
            <button type="button" data-line-qty="-1" aria-label="${S.dec}">${icon('minus')}</button>
            <input type="number" value="${l.qty}" min="0" max="24" aria-label="${S.qty}" data-line-input>
            <button type="button" data-line-qty="1" aria-label="${S.inc}">${icon('plus')}</button>
          </div>
        </div>
        <div><p class="cart-line__price">${kc(l.qty * info(l).price)}</p><button type="button" class="cart-line__remove" data-line-remove>${S.remove}</button></div>
      </li>`).join('');
  };
  const bump = () => $$('.hdr__count').forEach(c => { c.classList.remove('is-bump'); void c.offsetWidth; c.classList.add('is-bump'); });
  const addToCart = (id, qty, price, button) => {
    id = String(id);
    if (STORE) {
      clearCartError();
      if (button && button.disabled) return;
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      STORE.add(id, qty).then(lines => {
        cart = lines.map(line => ({ ...line })); renderCart(); bump(); openDialog('cart');
      }).catch(showCartError).finally(() => {
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); const form = button.closest('[data-split-products]'); if (form) form.dispatchEvent(new Event('change')); }
      });
      return;
    }
    const line = cart.find(l => l.id === id);
    if (line) line.qty = clamp(line.qty + qty, 1, 24); else cart.push({ id, qty, price });
    saveCart(); bump(); openDialog('cart');
  };
  const linesEl = $('[data-cart-lines]');
  if (linesEl) {
    linesEl.addEventListener('click', e => {
      const li = e.target.closest('[data-line]'); if (!li) return;
      const line = cart.find(l => String(l.key || l.id) === li.dataset.line);
      if (e.target.closest('[data-line-remove]')) cart = cart.filter(l => l !== line);
      const b = e.target.closest('[data-line-qty]');
      if (b) { line.qty += parseInt(b.dataset.lineQty, 10); if (line.qty < 1) cart = cart.filter(l => l !== line); }
      saveCart();
    });
    linesEl.addEventListener('change', e => {
      const li = e.target.closest('[data-line]'); if (!li || !e.target.matches('[data-line-input]')) return;
      const line = cart.find(l => String(l.key || l.id) === li.dataset.line);
      line.qty = clamp(parseInt(e.target.value, 10) || 0, 0, 24);
      if (!line.qty) cart = cart.filter(l => l !== line);
      saveCart();
    });
  }
  addEventListener('storage', e => { if (e.key === CART_KEY) { cart = readCart(); renderCart(); } });
  renderCart();
  if (STORE) STORE.refresh().then(lines => { cart = lines.map(line => ({ ...line })); renderCart(); }).catch(showCartError);

  /* ---------------------------------------------------------------- Product forms
     One radio group holds every option: the plain 500 ml bottle and the gift packagings. The "Gift packaging"
     chip opens the menu and picks the first packaging; choosing the plain bottle closes it again. Without JS
     the menu is always open, so the form still works.
     Everything marked data-for="<option keys>" (gallery slides and thumbs, shop photos) shows only for the picked option. */
  let mediaKey = null;
  const mediaSubs = new Set(); // the product gallery restarts at the first photo of the new option
  const showMedia = key => {
    if (key === mediaKey) return;
    mediaKey = key;
    $$('[data-for]').forEach(el => { el.hidden = !el.dataset.for.split(' ').includes(key); });
    $$('.pdp__slides').forEach(s => { s.scrollLeft = 0; }); // phones: the swipe strip starts at the new photo
    mediaSubs.forEach(fn => fn());
  };
  $$('[data-product-form]').forEach(form => {
    const reviewOnly = form.hasAttribute('data-preview-only');
    // Preview gift choices remain disabled without JS and never carry a variant ID.
    $$('input[data-preview-variant]', form).forEach(input => { input.disabled = false; });
    const choices = () => $$('input[data-key]', form);
    const qtyInput = $('input[name="quantity"]', form);
    const packs = $('[data-packs]', form), giftBtn = $('[data-gift-open]', form);
    const plain = $('input[data-key="none"]', form);
    const gifts = choices().filter(i => i.dataset.key !== 'none' && !i.disabled);
    const current = () => $('input[data-key]:checked', form) || plain;
    const giftMode = on => {
      if (packs) packs.hidden = !on;
      if (giftBtn) { giftBtn.setAttribute('aria-expanded', on); giftBtn.classList.toggle('is-on', on); }
    };
    const refresh = () => {
      const v = current(); if (!v) return; const q = clamp(parseInt(qtyInput && qtyInput.value, 10) || 1, 1, 24);
      const previewChoice = v.hasAttribute('data-preview-variant');
      const money = (reviewOnly || previewChoice) ? n => `${Math.round(n).toLocaleString('cs-CZ')} Kč` : kc;
      $$('[data-price-out]').forEach(el => { el.textContent = money(parseFloat(v.dataset.price) * (el.hasAttribute('data-price-total') ? q : 1)); });
      showMedia(v.dataset.key);
      const buyButton = $('button[type="submit"]', form);
      if (buyButton && form.hasAttribute('data-split-products')) {
        buyButton.disabled = previewChoice || v.disabled || !/^\d+$/.test(v.value) || buyButton.hasAttribute('aria-busy');
        const label = $('[data-buy-label]', buyButton);
        if (label) label.textContent = previewChoice ? buyButton.dataset.previewLabel : (v.disabled ? buyButton.dataset.soldOutLabel : buyButton.dataset.addLabel);
      }
    };
    if (giftBtn) giftBtn.hidden = false;
    giftMode(false);
    if (giftBtn) giftBtn.addEventListener('click', () => {
      giftMode(true);
      if (!gifts.some(i => i.checked) && gifts[0]) gifts[0].checked = true;
      refresh();
      const picked = gifts.find(i => i.checked); if (picked) picked.focus();
    });
    form.addEventListener('change', () => { giftMode(!(plain && plain.checked)); refresh(); });
    $$('[data-qty]', form).forEach(b => b.addEventListener('click', () => {
      qtyInput.value = clamp((parseInt(qtyInput.value, 10) || 1) + parseInt(b.dataset.qty, 10), 1, 24); refresh();
    }));
    if (qtyInput) qtyInput.addEventListener('input', refresh);
    form.addEventListener('submit', e => {
      e.preventDefault();
      if (reviewOnly) return;
      const v = current();
      if (v && !v.disabled && !v.hasAttribute('data-preview-variant')) addToCart(v.value, clamp(parseInt(qtyInput && qtyInput.value, 10) || 1, 1, 24), parseFloat(v.dataset.price), $('button[type="submit"]', form));
    });
    // ?gift=<key> (shop cards; ?gift=1 from Our Story = the heart box) preselects a gift packaging and opens the menu
    const params = new URLSearchParams(location.search);
    const variant = params.get('variant');
    const linkedVariant = !reviewOnly && choices().find(i => i.value === variant && !i.disabled);
    const gift = params.get('gift');
    if (linkedVariant) {
      linkedVariant.checked = true;
    } else if (gift) {
      const g = gifts.find(i => i.dataset.key === (gift === '1' ? 'heart' : gift));
      if (g) g.checked = true;
    }
    giftMode(!(plain && plain.checked));
    refresh();

    // Mobile sticky add-to-cart bar, shown once the main button has scrolled away
    const bar = $('[data-sticky-buy]'), mainBtn = $('button[type="submit"]', form);
    if (bar && mainBtn && form.hasAttribute('data-sticky')) {
      bar.hidden = false; document.body.classList.add('has-sticky-buy');
      $('[data-sticky-add]', bar).addEventListener('click', () => form.requestSubmit());
      let on = null;
      scrollSubs.add(() => {
        const next = mainBtn.getBoundingClientRect().bottom < 0;
        if (next === on) return null;
        on = next;
        return () => bar.classList.toggle('is-on', next);
      });
    }
  });
  $$('[data-quick-add]').forEach(b => b.addEventListener('click', () => addToCart(b.dataset.quickAdd, 1, parseFloat(b.dataset.price), b)));

  /* ---------------------------------------------------------------- Gift packaging picker (shop)
     Four packagings as tabs; the picked one opens below with its photo, description and price. */
  $$('[data-giftpick]').forEach(g => {
    const tabs = $$('[data-gift-tab]', g), panels = $$('[data-gift-panel]', g);
    const show = key => {
      tabs.forEach(t => { const on = t.dataset.giftTab === key; t.setAttribute('aria-selected', on); t.tabIndex = on ? 0 : -1; });
      panels.forEach(p => { p.hidden = p.dataset.giftPanel !== key; });
    };
    // one selection for the whole page: picking here also picks it in the buy panel (and its photo)
    const pick = key => {
      show(key);
      const radio = $(`[data-product-form] input[data-key="${key}"]`);
      if (radio && !radio.disabled && !radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
    };
    mediaSubs.add(() => { if (panels.some(p => p.dataset.giftPanel === mediaKey)) show(mediaKey); });
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => pick(t.dataset.giftTab));
      t.addEventListener('keydown', e => {
        const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        const next = tabs[(i + d + tabs.length) % tabs.length];
        next.focus(); pick(next.dataset.giftTab);
      });
    });
  });

  /* ---------------------------------------------------------------- Product gallery
     Desktop: one photo at a time on a sticky stage beside the buy form, picked with the thumbs or the arrows,
     so choosing a gift packaging and looking at it needs no scrolling. Phones: a swipe carousel (CSS).
     Only the slides of the option picked in the buy form take part (data-for, see showMedia). */
  $$('[data-gallery]').forEach(g => {
    const thumbs = $$('[data-thumb]', g), slides = $$('[data-slide]', g);
    const nav = $('[data-stage-nav]', g), count = $('[data-stage-count]', g);
    const shown = () => slides.map((s, i) => i).filter(i => !slides[i].hidden);
    let active = 0;
    const show = i => {
      const list = shown(); if (!list.length) return;
      active = list.includes(i) ? i : list[0];
      slides.forEach((s, j) => s.classList.toggle('is-active', j === active));
      thumbs.forEach((t, j) => t.setAttribute('aria-current', j === active));
      if (nav) { nav.hidden = list.length < 2; count.textContent = `${list.indexOf(active) + 1} / ${list.length}`; }
    };
    const step = d => { const list = shown(); show(list[(list.indexOf(active) + d + list.length) % list.length]); };
    thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
    $$('[data-stage-step]', g).forEach(b => b.addEventListener('click', () => step(parseInt(b.dataset.stageStep, 10))));
    g.addEventListener('keydown', e => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.target.closest('model-viewer')) return; // arrow keys turn the 3D bottle there
      e.preventDefault(); step(e.key === 'ArrowLeft' ? -1 : 1);
    });
    mediaSubs.add(() => show(-1));
    g.classList.add('is-stage');
    show(-1);
  });

  /* ---------------------------------------------------------------- 3D bottle (product page)
     <model-viewer>, Google's web component and the one Shopify uses for 3D product media, comes from jsDelivr
     only on pages with a model, once the page has loaded; each model fetches its GLB when it comes into view.
     Touch screens get a slowly turning bottle, so a swipe still moves the photo carousel. */
  const MODEL_VIEWER = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@4.1.0/dist/model-viewer.min.js';
  if ($('model-viewer')) {
    const loadViewer = () => {
      if (customElements.get('model-viewer') || $(`script[src="${MODEL_VIEWER}"]`)) return;
      const s = document.createElement('script');
      s.type = 'module'; s.src = MODEL_VIEWER;
      document.head.append(s);
    };
    const idle = window.requestIdleCallback ? fn => requestIdleCallback(fn, { timeout: 2500 }) : fn => setTimeout(fn, 1200);
    if (document.readyState === 'complete') idle(loadViewer); else addEventListener('load', () => idle(loadViewer), { once: true });
    $$('[data-3d]').forEach(el => el.addEventListener('click', loadViewer)); // picked right away: don't wait for idle
    if (!mqFine.matches) $$('.pdp__3d model-viewer').forEach(mv => mv.removeAttribute('camera-controls'));
  }

  /* ---------------------------------------------------------------- In-page tabs (product sections) */
  $$('[data-spy]').forEach(nav => {
    const links = $$('a[href^="#"]', nav);
    const targets = links.map(a => document.getElementById(a.hash.slice(1))).filter(Boolean);
    const io = new IntersectionObserver(es => es.forEach(e => {
      if (e.isIntersecting) links.forEach(a => a.classList.toggle('is-active', a.hash === `#${e.target.id}`));
    }), { rootMargin: '-35% 0px -60% 0px' });
    targets.forEach(t => io.observe(t));
  });

  /* ---------------------------------------------------------------- Forms (prototype submit states) */
  $$('form[data-demo-form]').forEach(form => form.addEventListener('submit', e => {
    e.preventDefault();
    if (!form.checkValidity()) { form.reportValidity(); return; }
    const box = document.createElement('div');
    box.className = 'form-success'; box.setAttribute('role', 'status'); box.tabIndex = -1;
    const title = document.createElement('strong'); title.textContent = form.dataset.successTitle || '';
    const text = document.createElement('p'); text.textContent = form.dataset.successText || '';
    box.append(title, text);
    form.replaceWith(box); box.focus();
  }));

  /* Withdrawal form: composes the withdrawal notice in the page's language and opens it in the email app
     (no-JS fallback: the form itself posts to mailto:). Shopify: {% form 'contact' %} or the Revoq app block. */
  $$('form[data-withdrawal]').forEach(form => form.addEventListener('submit', e => {
    e.preventDefault();
    if (!form.checkValidity()) { form.reportValidity(); return; }
    const d = form.dataset;
    let mail;
    try { mail = JSON.parse(d.mail); } catch (err) { form.submit(); return; }
    const field = n => form.elements[n];
    const value = n => {
      const el = field(n);
      if (!el || !el.value.trim()) return '';
      if (el.type === 'date') return new Date(`${el.value}T12:00:00`).toLocaleDateString(I18N.lang);
      if (el.tagName === 'SELECT') return el.selectedOptions[0].text;
      return el.value.trim();
    };
    const rows = mail.fields.map(([label, n]) => (value(n) ? `${label}: ${value(n)}` : null)).filter(Boolean);
    const body = [mail.head[0], '', mail.head[1], value('items'), '', ...rows, '', `${mail.date}: ${new Date().toLocaleDateString(I18N.lang)}`].join('\n');
    const href = `mailto:${mail.to}?subject=${encodeURIComponent(mail.subject.replace('%s', value('order')))}&body=${encodeURIComponent(body)}`;

    const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text) n.textContent = text; return n; };
    const box = el('div', 'form-success'); box.setAttribute('role', 'status'); box.tabIndex = -1;
    const open = el('a', 'btn btn--primary btn--sm', d.openLabel); open.href = href;
    const copy = el('button', 'btn btn--ghost btn--sm', d.copyLabel); copy.type = 'button';
    copy.addEventListener('click', () => {
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(body).then(() => { copy.textContent = d.copiedLabel; }, () => {});
    });
    const actions = el('div', 'form-success__actions'); actions.append(open, copy);
    box.append(el('strong', '', d.successTitle), el('p', '', d.successText), el('pre', '', body), actions, el('p', 'form-success__note', d.fallback));
    form.replaceWith(box); box.focus();
    open.click();
  }));

  /* ---------------------------------------------------------------- Newsletter pop-up
     Opens once the visitor has been on the site for 30 s (counted from the first page of the visit),
     but never on legal pages, over an open dialog, in a background tab or while someone is typing.
     Closing snoozes it for 14 days; signing up (here or in the homepage form) hides it for good.
     ?newsletter opens it straight away (for review). */
  const nlp = $('[data-nlp]');
  if (nlp) {
    const KEY = 'jdo-newsletter', DELAY = 30000, SNOOZE = 14 * 864e5;
    const saved = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; } };
    const remember = state => {
      if (saved().state === 'subscribed') return; // never downgrade a sign-up to "closed"
      try { localStorage.setItem(KEY, JSON.stringify({ state, at: Date.now() })); } catch (e) { /* storage blocked */ }
    };
    let arrived = Date.now();
    try { arrived = Number(sessionStorage.getItem('jdo-arrived')) || arrived; sessionStorage.setItem('jdo-arrived', arrived); } catch (e) { /* storage blocked */ }
    const card = $('.nlp__card', nlp);
    let timer = 0, doneTimer = 0;
    const isOpen = () => nlp.classList.contains('is-open');
    const busy = () => document.hidden || root.classList.contains('has-dialog') ||
      (document.activeElement && document.activeElement.matches('input, textarea, select'));
    const show = () => {
      if (isOpen()) return;
      if (busy()) { timer = setTimeout(show, 4000); return; }
      nlp.classList.add('is-open');
      try { nlp.showPopover(); } catch (e) { /* no popover support: stays a fixed layer */ }
      requestAnimationFrame(() => requestAnimationFrame(() => nlp.classList.add('is-in')));
      if (document.activeElement === document.body) card.focus({ preventScroll: true });
    };
    const hide = (state = 'closed') => {
      if (!isOpen()) return;
      clearTimeout(doneTimer);
      remember(state);
      nlp.classList.remove('is-in');
      const hadFocus = nlp.contains(document.activeElement);
      setTimeout(() => {
        nlp.classList.remove('is-open', 'is-done');
        try { nlp.hidePopover(); } catch (e) { /* not a popover */ }
        if (hadFocus && document.activeElement) document.activeElement.blur();
      }, reduced() ? 200 : 380);
    };
    $$('[data-nlp-close]', nlp).forEach(b => b.addEventListener('click', () => hide()));
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && isOpen() && !root.classList.contains('has-dialog')) hide(); });
    // runs after the prototype submit handler above (which swaps the form for the thank-you message)
    $$('form[data-newsletter]').forEach(f => {
      const inPopup = nlp.contains(f); // checked up front: by submit time the form has been swapped out
      f.addEventListener('submit', () => {
        if (!f.checkValidity()) return;
        remember('subscribed');
        clearTimeout(timer);
        if (inPopup) { nlp.classList.add('is-done'); doneTimer = setTimeout(() => hide('subscribed'), 6000); }
      });
    });

    const s = saved();
    const snoozed = s.state === 'subscribed' || (s.state === 'closed' && Date.now() - s.at < SNOOZE);
    if (/[?&]newsletter\b/.test(location.search)) show();
    else if (!snoozed && !document.body.hasAttribute('data-no-popup')) timer = setTimeout(show, Math.max(1000, DELAY - (Date.now() - arrived)));
  }

  /* ---------------------------------------------------------------- Background videos
     <video data-lazy-video muted loop playsinline preload="none" poster="…"><source data-src="…"></video>
     Loaded only when near the viewport; skipped entirely under reduced motion or Save-Data (poster stays).
     Inside the sticky steps only the active chapter's video plays. */
  const vids = $$('video[data-lazy-video]');
  const noVideo = reduced() || (navigator.connection && navigator.connection.saveData);
  const near = new Set();
  const canPlay = v => near.has(v) && !v.dataset.userPaused && (!v.closest('[data-step-media]') || v.closest('[data-step-media]').classList.contains('is-active'));
  const syncVideo = v => {
    if (noVideo) return;
    if (canPlay(v)) {
      if (!v.dataset.loaded) { $$('source[data-src]', v).forEach(s => { s.src = s.dataset.src; }); v.load(); v.dataset.loaded = 1; }
      const p = v.play(); if (p) p.catch(() => {});
    } else if (!v.paused) v.pause();
  };
  syncVideos = () => vids.forEach(syncVideo);
  // Posters are lazy too (a poster attribute would be fetched on page load for every video)
  const posterIO = 'IntersectionObserver' in window ? new IntersectionObserver(es => es.forEach(e => {
    if (!e.isIntersecting) return;
    e.target.poster = e.target.dataset.poster; posterIO.unobserve(e.target);
  }), { rootMargin: '100% 0px' }) : null;
  vids.forEach(v => { if (!v.dataset.poster) return; if (posterIO) posterIO.observe(v); else v.poster = v.dataset.poster; });
  if (noVideo) root.classList.add('no-video');
  else if (vids.length) {
    const vio = new IntersectionObserver(es => es.forEach(e => {
      if (e.isIntersecting) near.add(e.target); else near.delete(e.target);
      syncVideo(e.target);
    }), { rootMargin: '25% 0px' });
    vids.forEach(v => vio.observe(v));
    document.addEventListener('visibilitychange', () => { if (document.hidden) vids.forEach(v => v.pause()); else syncVideos(); });
  }
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-video-toggle]'); if (!b) return;
    const v = b.parentElement.querySelector('video'); if (!v) return;
    const pause = b.getAttribute('aria-pressed') !== 'true';
    if (pause) v.dataset.userPaused = 1; else delete v.dataset.userPaused;
    b.setAttribute('aria-pressed', pause);
    b.setAttribute('aria-label', pause ? b.dataset.labelPlay : b.dataset.labelPause);
    syncVideo(v);
  });

})();
